import { apiGet } from '../../../adapters/api-client/api';
import { MyCourse } from './courses';

/** The window every figure on the dashboard covers (TCH-07-FR-02). */
export interface MetricPeriod {
  from: string;
  to: string;
  label: string;
}

/** A metric ADR-007 asks for that nothing can produce yet, and why. */
export interface UnavailableMetric {
  requirement: string;
  metric: string;
  reason: string;
}

export interface CoursePerformance {
  courseId: string;
  title: string;
  status: string;
  learners: number;
  /** 0–1, or null when nobody has enrolled. */
  completionRate: number | null;
}

export interface TeacherDashboardMetrics {
  totalStudents: number;
  activeEnrollments: number;
  publishedCourses: number;
  draftCourses: number;
  earnedInPeriod: number;
  releasedToDate: number;
  pending: number;
  disputed: number;
  /** Null when there is nothing to average, which is not the same as zero. */
  averageCompletionRate: number | null;
}

export type AttentionKind =
  | 'SESSION_SOON'
  | 'BOOKING_AWAITING_YOU'
  | 'EVIDENCE_PENDING'
  | 'DISPUTE_OPEN'
  | 'NO_AVAILABILITY'
  | 'NO_COURSES'
  | 'DRAFTS_UNPUBLISHED';

/** Something the teacher should do, most costly first. */
export interface AttentionItem {
  kind: AttentionKind;
  count?: number;
  at?: string;
  amount?: number;
}

export interface TeacherDashboard {
  teacherId: string;
  period: MetricPeriod;
  currency: string;
  metrics: TeacherDashboardMetrics;
  courses: CoursePerformance[];
  needsAttention: AttentionItem[];
  /** Sources the attention list could not read. */
  attentionIncomplete: string[];
  unavailable: UnavailableMetric[];
}

/** S5.1 — the whole dashboard in one authoritative payload. */
export function getTeacherDashboard(): Promise<TeacherDashboard> {
  return apiGet<TeacherDashboard>('/api/v1/teachers/me/dashboard');
}

/**
 * A course as its own teacher sees it: the record, plus the engagement the
 * list screen needs (TCH-08-FR-02 / FR-05).
 *
 * Extends MyCourse rather than restating it, so a field added to the course
 * cannot silently go missing here. Computed server-side alongside the
 * dashboard so the two screens cannot disagree about how many students a
 * teacher has.
 */
export interface TeacherCourseSummary extends MyCourse {
  /** Distinct children who hold or held a seat. Not enrollments. */
  learners: number;
  activeEnrollments: number;
  /** 0–1, or null when nobody has enrolled. */
  completionRate: number | null;
}

export function getMyCoursesWithEngagement(): Promise<{ courses: TeacherCourseSummary[] }> {
  return apiGet('/api/v1/teachers/me/courses');
}
